import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendlyButton } from "@/components/landing/CalendlyButton";
import { SITE } from "@/lib/siteConfig";

const HERO_BG =
  "https://images.unsplash.com/photo-1614850523011-8f49ffc73908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwZmlsbSUyMGdyYWluJTIwYWJzdHJhY3QlMjBuYXZ5JTIwYmx1ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MzYwOTY4fDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.22, 1, 0.36, 1];

// Masked line-by-line reveal for the headline.
const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy-900 text-warm-neutral"
    >
      {/* Parallax abstract background */}
      <motion.div
        aria-hidden="true"
        style={{ y: bgY }}
        className="absolute inset-0 -top-[10%] h-[120%]"
      >
        <img
          src={HERO_BG}
          alt=""
          className="h-full w-full object-cover opacity-[0.18]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/40 via-navy-900/70 to-navy-900" />
      </motion.div>
      <div className="grain-overlay" aria-hidden="true" />

      <motion.div
        style={{ y: contentY }}
        className="container-page relative z-10 pt-32 pb-20 md:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-warm-accent mb-8"
        >
          {SITE.location} &nbsp;·&nbsp; Bookkeeping for Small Businesses
        </motion.p>

        <h1 className="font-heading font-semibold text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight max-w-5xl">
          <Line delay={0.25}>Stop Guessing</Line>
          <Line delay={0.35}>Whether Your Books</Line>
          <Line delay={0.45}>
            Are <span className="italic font-normal text-warm-accent">Right</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="font-body text-base sm:text-lg leading-relaxed text-warm-neutral/80 max-w-2xl mt-8"
        >
          If your bookkeeping is behind, confusing, or taking too much of your
          time, {SITE.shortName} can help you understand what needs attention and
          determine the right way forward.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-10 flex flex-col items-start gap-5"
        >
          <CalendlyButton testId="hero-cta-button" variant="light" size="lg">
            Schedule a Free 30-Minute Bookkeeping Consultation
          </CalendlyButton>
          <p className="font-body text-sm text-warm-neutral/60 max-w-xl">
            No pressure. Brown &amp; Associates leads the conversation &mdash; I&rsquo;ll
            ask questions about your business and your bookkeeping concerns to
            determine what service or next step may be appropriate. It does not
            include reviewing your accounting file, troubleshooting individual
            transactions, correcting the books, or providing detailed bookkeeping
            advice.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
